// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState } from 'react';
import { TextInput, TextArea, Select, SelectItem } from '@carbon/react';
import { validatePolicy } from '../../shared/procedures';
import type { PolicyDetail } from '../../shared/types/procedures';
import formStyles from './ProcedureForm.module.css';
import styles from './PolicyForm.module.css';

interface PolicyFormProps {
  policy: PolicyDetail;
  original: PolicyDetail;
  onChange: (next: PolicyDetail) => void;
  /** Create mode has no prior version to diff against — hides the
   *  "Changes since" summary rather than showing a meaningless diff of a
   *  blank draft against itself. */
  hideChanges?: boolean;
}

type RequiredFieldName =
  | 'title'
  | 'policyNumber'
  | 'classification'
  | 'policyDomain'
  | 'scope'
  | 'enforcementPoint'
  | 'decisionSummary'
  | 'package';

// Real enums from `docs/upstream-specs/sop-factory.yaml`'s `Domain`,
// `PolicyDomain`, `PolicyScope`, and `EnforcementPoint` schemas — hardcoded
// rather than derived from existing library data (as `ProcedureForm`'s
// classification/equipment-type dropdowns are) since a fresh policy draft
// has no prior policy artifacts to derive options from anyway.
const DOMAIN_OPTIONS = ['cooling', 'power', 'fire-life-safety', 'controls', 'shared'];
const POLICY_DOMAIN_OPTIONS = [
  'ticketing-alarming',
  'approvals',
  'qualifications',
  'safety-loto-ppe',
  'redundancy',
  'severity-classification',
  'scheduling-conflict',
  'energy-envelope',
];
const SCOPE_OPTIONS = ['global', 'site', 'equipment-class', 'equipment-type'];
const ENFORCEMENT_POINT_OPTIONS = ['pre-exec-gate', 'runtime-guard', 'classification-routing'];

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children} <span className={formStyles.required}>*</span>
    </>
  );
}

const CHANGE_LABELS: [keyof PolicyDetail, string][] = [
  ['title', 'Title'],
  ['policyNumber', 'Policy #'],
  ['version', 'Version'],
  ['classification', 'Classification'],
  ['policyDomain', 'Policy domain'],
  ['scope', 'Scope'],
  ['enforcementPoint', 'Enforcement point'],
  ['decisionSummary', 'Decision summary'],
  ['package', 'Package'],
  ['files', 'Files'],
];

function diffLabels(original: PolicyDetail, current: PolicyDetail): string[] {
  return CHANGE_LABELS.filter(
    ([field]) => JSON.stringify(original[field]) !== JSON.stringify(current[field])
  ).map(([, label]) => label);
}

function FormSection({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={formStyles.section}>
      <div className={formStyles.sectionHead}>
        <h2 className={formStyles.sectionLabel}>
          {label}
          {required && <span className={formStyles.required}>*</span>}
        </h2>
      </div>
      {children}
    </section>
  );
}

/**
 * Guided-form view of a policy — the policy-as-code counterpart of
 * `ProcedureForm`. Fully editable local UI state — no backend field or
 * endpoint persists any of it yet (see `useCreatePolicy`'s docstring), so
 * closing/reopening this page discards whatever was changed.
 */
export default function PolicyForm({
  policy,
  original,
  onChange,
  hideChanges = false,
}: PolicyFormProps) {
  const changes = diffLabels(original, policy);
  const errors = validatePolicy(policy);

  // Required-field errors only show once a field has been visited and left
  // (blur) — a brand-new blank draft shouldn't open already covered in red.
  const [touched, setTouched] = useState<Partial<Record<RequiredFieldName, boolean>>>({});
  function markTouched(field: RequiredFieldName) {
    setTouched(prev => ({ ...prev, [field]: true }));
  }
  function fieldError(field: RequiredFieldName): string | undefined {
    return touched[field] ? errors[field] : undefined;
  }

  function update<K extends keyof PolicyDetail>(key: K, value: PolicyDetail[K]) {
    onChange({ ...policy, [key]: value });
  }

  function updateFile(field: keyof PolicyDetail['files'], value: string) {
    onChange({ ...policy, files: { ...policy.files, [field]: value } });
  }

  return (
    <div className={formStyles.form}>
      <FormSection label="Metadata">
        <TextInput
          id="policy-title"
          labelText={<RequiredLabel>Title</RequiredLabel>}
          value={policy.title}
          onChange={e => update('title', e.target.value)}
          onBlur={() => markTouched('title')}
          invalid={Boolean(fieldError('title'))}
          invalidText={fieldError('title')}
        />
        <div className={formStyles.metadataRow}>
          <TextInput
            id="policy-number"
            labelText={<RequiredLabel>Policy #</RequiredLabel>}
            value={policy.policyNumber}
            onChange={e => update('policyNumber', e.target.value)}
            onBlur={() => markTouched('policyNumber')}
            invalid={Boolean(fieldError('policyNumber'))}
            invalidText={fieldError('policyNumber')}
          />
          <TextInput
            id="policy-version"
            labelText="Version"
            value={`v${policy.version}`}
            disabled
          />
          <Select
            id="policy-classification"
            labelText={<RequiredLabel>Classification</RequiredLabel>}
            value={policy.classification}
            onChange={e => update('classification', e.target.value)}
            onBlur={() => markTouched('classification')}
            invalid={Boolean(fieldError('classification'))}
            invalidText={fieldError('classification')}
          >
            <SelectItem value="" text="Select" />
            {DOMAIN_OPTIONS.map(option => (
              <SelectItem key={option} value={option} text={option} />
            ))}
          </Select>
          <Select
            id="policy-domain"
            labelText={<RequiredLabel>Policy domain</RequiredLabel>}
            value={policy.policyDomain}
            onChange={e => update('policyDomain', e.target.value)}
            onBlur={() => markTouched('policyDomain')}
            invalid={Boolean(fieldError('policyDomain'))}
            invalidText={fieldError('policyDomain')}
          >
            <SelectItem value="" text="Select" />
            {POLICY_DOMAIN_OPTIONS.map(option => (
              <SelectItem key={option} value={option} text={option} />
            ))}
          </Select>
        </div>
        <div className={formStyles.metadataRow2}>
          <Select
            id="policy-scope"
            labelText={<RequiredLabel>Scope</RequiredLabel>}
            value={policy.scope}
            onChange={e => update('scope', e.target.value)}
            onBlur={() => markTouched('scope')}
            invalid={Boolean(fieldError('scope'))}
            invalidText={fieldError('scope')}
          >
            <SelectItem value="" text="Select" />
            {SCOPE_OPTIONS.map(option => (
              <SelectItem key={option} value={option} text={option} />
            ))}
          </Select>
          <Select
            id="policy-enforcement-point"
            labelText={<RequiredLabel>Enforcement point</RequiredLabel>}
            value={policy.enforcementPoint}
            onChange={e => update('enforcementPoint', e.target.value)}
            onBlur={() => markTouched('enforcementPoint')}
            invalid={Boolean(fieldError('enforcementPoint'))}
            invalidText={fieldError('enforcementPoint')}
          >
            <SelectItem value="" text="Select" />
            {ENFORCEMENT_POINT_OPTIONS.map(option => (
              <SelectItem key={option} value={option} text={option} />
            ))}
          </Select>
        </div>
        <TextArea
          id="policy-decision-summary"
          labelText={<RequiredLabel>Decision summary</RequiredLabel>}
          value={policy.decisionSummary}
          onChange={e => update('decisionSummary', e.target.value)}
          onBlur={() => markTouched('decisionSummary')}
          rows={3}
          invalid={Boolean(fieldError('decisionSummary'))}
          invalidText={fieldError('decisionSummary')}
        />
      </FormSection>

      <FormSection label="Policy as code" required>
        <div className={formStyles.metadataRow2}>
          <TextInput
            id="policy-package"
            labelText={<RequiredLabel>Package</RequiredLabel>}
            placeholder="datacenter.peakshave.envelope"
            value={policy.package}
            onChange={e => update('package', e.target.value)}
            onBlur={() => markTouched('package')}
            invalid={Boolean(fieldError('package'))}
            invalidText={fieldError('package')}
          />
          <TextInput id="policy-language" labelText="Language" value="rego" disabled />
        </div>
        <div className={styles.filesRow}>
          <TextInput
            id="policy-file-policy"
            labelText="Policy file"
            placeholder="policies/peakshave/envelope.rego"
            value={policy.files.policy}
            onChange={e => updateFile('policy', e.target.value)}
          />
          <TextInput
            id="policy-file-data"
            labelText="Data file"
            placeholder="policies/peakshave/data.json"
            value={policy.files.data}
            onChange={e => updateFile('data', e.target.value)}
          />
          <TextInput
            id="policy-file-tests"
            labelText="Tests file"
            placeholder="policies/peakshave/envelope_test.rego"
            value={policy.files.tests}
            onChange={e => updateFile('tests', e.target.value)}
          />
        </div>
      </FormSection>

      {!hideChanges && (
        <div className={formStyles.changesBlock}>
          <h2 className={formStyles.changesLabel}>Changes since v{original.version}</h2>
          {changes.length === 0 ? (
            <div className={formStyles.emptyNote}>No changes yet.</div>
          ) : (
            <ul className={formStyles.changesList}>
              {changes.map(label => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
